import { Reading } from '../models/reading';

/** A reading plus a key unique per device: the API's (time, topic), with the time at full precision. */
export interface KeyedReading extends Reading {
  key: string;
}

/** One `GetDeviceDataByTimeRange` answer: readings in [from, to), newest first. */
export interface HistoryWindow {
  readings: KeyedReading[];
  truncated: boolean;
}

export type FetchWindow = (from: Date, to: Date) => Promise<HistoryWindow>;

/** The longest window the API accepts. */
export const MAX_WINDOW_MS = 31 * 24 * 60 * 60 * 1000;

/** Without a creation date, this many empty windows in a row (about three months) are taken as the start. */
export const EMPTY_WINDOWS_TO_STOP = 3;

export interface ReadHistoryOptions {
  now: Date;
  /** The device's creation date: nothing older is asked for. */
  since?: Date;
  onProgress?: (count: number) => void;
}

/**
 * Walks a device's history backwards with bounded time-range requests and returns every reading, oldest first.
 *
 * A window that comes back truncated holds only its newest readings, so it is asked again up to the oldest one
 * received. Dates carry milliseconds while the API keeps finer times, so the next `to` is that millisecond plus one:
 * the readings inside it come back again and are skipped by key, and nothing is lost to the rounding.
 */
export const readHistory = async (
  fetchWindow: FetchWindow,
  { now, since, onProgress }: ReadHistoryOptions,
): Promise<Reading[]> => {
  const readings: Reading[] = [];
  let to = now.getTime();
  let emptyInARow = 0;
  // Keys already taken from the millisecond the current `to` overlaps; empty unless the last window was truncated.
  let overlap = new Set<string>();

  for (;;) {
    const from = Math.max(to - MAX_WINDOW_MS, since ? since.getTime() : -Infinity);
    if (from >= to) break;

    const window = await fetchWindow(new Date(from), new Date(to));
    for (const r of window.readings) {
      if (!overlap.has(r.key)) readings.push({ time: r.time, topic: r.topic, payload: r.payload });
    }
    onProgress?.(readings.length);

    if (window.truncated) {
      const oldest = window.readings[window.readings.length - 1];
      const next = oldest.time.getTime() + 1;
      // More readings in one millisecond than the server cap: no `to` can make progress.
      if (next >= to) throw new Error('Too many readings at one instant to page through');
      overlap = new Set(window.readings.filter((r) => r.time.getTime() === oldest.time.getTime()).map((r) => r.key));
      to = next;
      emptyInARow = 0;
      continue;
    }

    overlap = new Set();
    emptyInARow = window.readings.length ? 0 : emptyInARow + 1;
    if (!since && emptyInARow >= EMPTY_WINDOWS_TO_STOP) break;
    to = from;
  }

  return readings.reverse();
};
